import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";

const TASK_TYPE_OPTIONS = [
  { value: "",             label: "All Team",      icon: "👥" },
  { value: "self",         label: "Self",          icon: "🙋" },
  { value: "subordinates", label: "Subordinates",  icon: "👇" },
  { value: "my_key_tasks", label: "My Key Tasks",  icon: "🔑" },
  { value: "api_tasks",    label: "API Tasks",     icon: "⚙️" },
  { value: "occurrence",   label: "Occurrence",    icon: "🔁" },
];

const OPTION_COLORS = {
  "":             "#94A3B8",
  "self":         "#7C3AED",
  "subordinates": "#0D9488",
  "my_key_tasks": "#D97706",
  "api_tasks":    "#2563EB",
  "occurrence":   "#059669",
};

const getLabel = val => TASK_TYPE_OPTIONS.find(o => o.value === val)?.label || "All Team";
const getColor = val => OPTION_COLORS[val] || "#94A3B8";

export default function TeamDropdown({ value = "", onChange, allowedValues = null }) {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef          = useRef(null);

  // Outside click — close dropdown
  useEffect(() => {
    const fn = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const isSelected = value !== "";

  const baseOptions = allowedValues
    ? TASK_TYPE_OPTIONS.filter(o => allowedValues.includes(o.value))
    : TASK_TYPE_OPTIONS;

  const filtered = baseOptions.filter(o =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpen = () => {
    setSearch("");
    setOpen(prev => !prev);
  };

  const select = val => {
    onChange(val);
    setOpen(false);
    setSearch("");
  };

  return (
    <div ref={wrapperRef} className="relative w-full">

      {/* ── Trigger ── */}
      <div
        onClick={handleOpen}
        className={`flex items-center gap-2 min-h-[34px] px-3 rounded-lg cursor-pointer bg-white select-none transition-all duration-150 border ${
          open
            ? "border-violet-500 ring-2 ring-violet-100"
            : "border-gray-200 shadow-sm hover:bg-gray-50"
        }`}
      >
        {!isSelected ? (
          // All Team — placeholder style
          <span className="text-xs text-slate-400 flex-1">All Team</span>
        ) : (
          // Selected option
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0 bg-[var(--opt-color)]"
              style={{ "--opt-color": getColor(value) }}
            >
              {getLabel(value).charAt(0).toUpperCase()}
            </div>
            <span className="text-xs font-semibold text-slate-700 truncate">
              {getLabel(value)}
            </span>
          </div>
        )}

        {/* Right — clear + chevron */}
        <div className="ml-auto flex items-center gap-1 shrink-0">
          {isSelected && (
            <span
              onClick={e => { e.stopPropagation(); onChange(""); }}
              className="w-4 h-4 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 text-xs cursor-pointer hover:bg-red-50 hover:text-red-400 transition-colors"
            >
              ×
            </span>
          )}
          <ChevronDown
            size={12}
            className={`text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </div>
      </div>

      {/* ── Dropdown ── */}
      {open && (
        <div className="absolute top-full left-0 mt-1.5 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xl w-full z-50">

          {/* Options — no search, only 5 options */}
          <div className="py-1">
            {filtered.map((opt, i) => {
              const checked = value === opt.value;
              const isAll   = opt.value === "";
              return (
                <div
                  key={opt.value}
                  onClick={() => select(opt.value)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-colors
                    ${checked ? "bg-violet-50" : "hover:bg-slate-50"}
                    ${i < filtered.length - 1 ? "border-b border-slate-50" : ""}`}
                >
                  {/* Radio circle */}
                  <div
                    className={`w-4 h-4 rounded-full shrink-0 flex items-center justify-center border-2 transition-all ${
                      checked ? "border-violet-600 bg-violet-600" : "border-slate-300 bg-white"
                    }`}
                  >
                    {checked && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>

                  {isAll ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-[11px] shrink-0 bg-slate-100">
                        {opt.icon}
                      </div>
                      <span className={`text-xs ${checked ? "font-bold text-violet-700" : "font-medium text-slate-500"}`}>
                        All Team
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0 bg-[var(--opt-color)]"
                        style={{ "--opt-color": getColor(opt.value) }}
                      >
                        {opt.label.charAt(0).toUpperCase()}
                      </div>
                      <span className={`text-xs ${checked ? "font-bold text-violet-700" : "font-medium text-slate-700"}`}>
                        {opt.label}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}